import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

// Backup for the F2Pool API: read-only page links, one per F2Pool account.
// When the API is down or refuses this server's IP, those accounts' machines
// are read from their pages instead.
const F2poolReadonlyLinks = () => {
  const [links, setLinks] = useState([]);
  const [status, setStatus] = useState({});
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () =>
    axios.get(`${API}/f2pool/readonly-links`)
      .then((r) => { setLinks(r.data.links || []); setStatus(r.data); })
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!url.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/f2pool/readonly-links`, { url: url.trim() });
      setLinks(r.data.links || []);
      setUrl("");
      const counts = Object.entries(r.data.workers || {}).map(([c, n]) => `${n} ${c}`).join(", ");
      toast.success(`Added ${r.data.account}${counts ? ` — ${counts} machines` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that link");
    }
    setAdding(false);
  };

  const remove = async (l) => {
    if (!window.confirm(`Remove the read-only link for ${l.account}?`)) return;
    try {
      const r = await axios.delete(`${API}/f2pool/readonly-links/${l.key}`);
      setLinks(r.data.links || []);
    } catch {
      toast.error("Could not remove it");
    }
  };

  return (
    <div className="border-t border-[#27272A] pt-4 space-y-3" data-testid="f2pool-readonly-links">
      <div>
        <div className="text-sm font-bold text-white">Backup: read-only links</div>
        <p className="text-[11px] text-gray-500 mt-0.5">
          If the F2Pool API is down or asks for the IP to be whitelisted, machines for these accounts are read from
          their read-only page instead. Add one link per F2Pool account (f2pool.com → read-only page, "hashrate &amp; worker list" only).
        </p>
      </div>
      {status.source === "readonly" && (
        <div className="text-xs rounded-lg p-2 border border-amber-500/40 bg-amber-500/10 text-amber-300">
          ⚠️ Running on the backup now{status.blocked ? ` — API says: ${status.blocked}` : ""}.
        </div>
      )}
      {links.length > 0 && (
        <div className="space-y-1">
          {links.map((l) => (
            <div key={l.key} className="flex items-center justify-between gap-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm text-white">{l.account}</div>
                <div className="text-[11px] text-gray-500 font-mono truncate">
                  {Object.entries(l.workers || {}).map(([c, n]) => `${n} ${c}`).join(" · ") || "no machines when added"} · …{l.key.slice(-6)}
                </div>
              </div>
              <button onClick={() => remove(l)} className="text-xs text-red-400 hover:text-red-300 shrink-0">Remove</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 text-sm text-white font-mono outline-none focus:border-[#F59E0B]"
          placeholder="https://www.f2pool.com/mining-user/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button onClick={add} disabled={adding}
                className="px-3 py-2 rounded-md bg-[#F59E0B] text-black text-sm font-bold disabled:opacity-50 shrink-0">
          {adding ? "Checking…" : "+ Add link"}
        </button>
      </div>
    </div>
  );
};

export default F2poolReadonlyLinks;
